import { useState } from 'react';
import { Icon } from '@garnet/ui';

export interface Tweaks {
  theme: 'light' | 'dark';
  density: 'compact' | 'regular' | 'comfortable';
  accent: 'subtle' | 'standard' | 'vivid';
  mono: 'balanced' | 'forward';
  shell: 'rail' | 'list' | 'command';
}

export const TWEAK_DEFAULTS: Tweaks = {
  theme: 'light',
  density: 'regular',
  accent: 'standard',
  mono: 'balanced',
  shell: 'rail',
};

function TweakRadio<V extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: V;
  options: readonly V[];
  onChange: (v: V) => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <span className="label" style={{ fontSize: 8.5 }}>
        {label}
      </span>
      <div style={{ display: 'flex', gap: 0, border: '1px solid var(--border)', borderRadius: 4, overflow: 'hidden' }}>
        {options.map((o, i) => (
          <button
            key={o}
            onClick={() => onChange(o)}
            className="mono"
            aria-pressed={value === o}
            style={{
              flex: 1,
              padding: '5px 6px',
              fontSize: 10.5,
              textTransform: 'capitalize',
              background: value === o ? 'var(--garnet-muted)' : 'transparent',
              color: value === o ? 'var(--garnet-text)' : 'var(--fg-2)',
              borderLeft: i ? '1px solid var(--border-subtle)' : 'none',
            }}
          >
            {o}
          </button>
        ))}
      </div>
    </div>
  );
}

export function TweaksPanel({
  tweaks,
  setTweak,
  onReset,
}: {
  tweaks: Tweaks;
  setTweak: <K extends keyof Tweaks>(key: K, value: Tweaks[K]) => void;
  onReset: () => void;
}) {
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        title="Tweaks"
        aria-label="Open tweaks"
        style={{
          position: 'fixed',
          right: 16,
          bottom: 40,
          zIndex: 50,
          width: 38,
          height: 38,
          borderRadius: 9,
          border: '1px solid var(--border)',
          background: 'var(--bg-surface)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 6px 20px rgba(0,0,0,0.18)',
        }}
      >
        <Icon name="filter" size={16} color="var(--garnet)" />
      </button>
    );
  }

  return (
    <div
      className="panel"
      style={{
        position: 'fixed',
        right: 16,
        bottom: 40,
        zIndex: 50,
        width: 240,
        padding: 14,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        boxShadow: '0 12px 40px rgba(0,0,0,0.22)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span className="panel-title">Tweaks</span>
        <button className="btn btn-ghost btn-sm" aria-label="Close tweaks" onClick={() => setOpen(false)}>
          <Icon name="x" size={13} />
        </button>
      </div>
      <TweakRadio label="Theme" value={tweaks.theme} options={['light', 'dark']} onChange={(v) => setTweak('theme', v)} />
      <TweakRadio
        label="Density"
        value={tweaks.density}
        options={['compact', 'regular', 'comfortable']}
        onChange={(v) => setTweak('density', v)}
      />
      <TweakRadio
        label="Accent"
        value={tweaks.accent}
        options={['subtle', 'standard', 'vivid']}
        onChange={(v) => setTweak('accent', v)}
      />
      <TweakRadio label="Mono" value={tweaks.mono} options={['balanced', 'forward']} onChange={(v) => setTweak('mono', v)} />
      <div style={{ height: 1, background: 'var(--border-subtle)' }} />
      <TweakRadio label="Shell" value={tweaks.shell} options={['rail', 'list', 'command']} onChange={(v) => setTweak('shell', v)} />
      <div style={{ height: 1, background: 'var(--border-subtle)' }} />
      <button className="btn btn-sm mono" onClick={onReset}>
        <Icon name="refresh" size={12} />
        Reset to seed corpus
      </button>
    </div>
  );
}
